



































































import {EstimateEstateType, EstimateState} from '@/model/api';
import {BadResponseError} from '@/model/errors/BadResponseError';
import {ErrorWithUserMessage} from '@/model/errors/ErrorWithUserMessage';
import {NetworkError} from '@/model/errors/NetworkError';
import {Estimate} from '@/model/Estimate';
import {log} from '@/model/logger';
import {NotSubmittedParameters, OfflineManager} from '@/model/offline';
import {shouldNotHappen} from '@/utils';
import {isLeft} from 'fp-ts/lib/Either';
import {Component, Inject, Prop, Provide, Ref, Vue} from 'vue-property-decorator';
import VueRouter from 'vue-router';
import VueScrollTo from 'vue-scrollto';
import NotSubmittedDataDialog from './NotSubmittedDataDialog.vue';
import {Segment, SegmentConfiguration} from './segments';
import SyncDialog from './SyncDialog.vue';

@Component({ components: {NotSubmittedDataDialog, SyncDialog} })
export default class EstimateForm<T extends EstimateState> extends Vue {

    public get segmentConfigurations(): SegmentConfiguration[] {
        return this.segmentConfigurationsFactory(this.lastKnownEstimateState);
    }
    @Inject()
    public readonly model: Estimate<T>;

    @Inject()
    public readonly notSubmittedParameters: NotSubmittedParameters;

    @Inject()
    public readonly offlineManager: OfflineManager<T>;

    @Prop({type: Object, required: true})
    public readonly estimate: T;

    @Prop({type: Function, required: true})
    public readonly segmentConfigurationsFactory: (estimate: T) => SegmentConfiguration[];

    @Prop({type: Boolean, default: true})
    public readonly showFinishButton: boolean;

    @Ref()
    protected readonly segments: Array<Segment<T>>;

    @Ref()
    protected readonly syncDialog: SyncDialog;

    @Ref()
    protected readonly notSubmittedDataDialog: NotSubmittedDataDialog;

    @Provide()
    public readonly estateType: EstimateEstateType = this.estimate.estateType;

    protected errorMessage: string | null = null;

    private accessibleSegments: number[] = [];

    public currentSegment: number = 0;

    private loading: boolean = false;

    protected online: boolean = true;

    protected lastKnownEstimateState: T;

    public canCreateEstimate(): boolean {
        return this.segments?.slice(this.currentSegment + 1).every(segment => segment.allowsMovingOn()) || false;
    }

    public data() {
        return {
            lastKnownEstimateState: undefined, // Make property reactive
        };
    }

    public submitSegmentAndContinue(): void {
        this.save()
            .then(success => {
                if (!success) {
                    return;
                }

                return this.$router.push(this.segmentConfigurations[this.currentSegment + 1].path);
            })
            .catch(error => {
                if (VueRouter.isNavigationFailure(error, VueRouter.NavigationFailureType.redirected)) {
                    // Pushed destination redirected, no problem there
                    // see https://github.com/vuejs/vue-router/issues/2932
                    return;
                }

                log(error);
            });
    }

    public async createEstimate(): Promise<void> {
        this.errorMessage = null;

        const segmentSubmitSuccessful = await this.submitCurrentSegment();

        if (!segmentSubmitSuccessful) {
            return;
        }

        const estimateUrlOrError = await this.model.createEstimate();

        if (isLeft(estimateUrlOrError)) {
            const error = estimateUrlOrError.left;

            if (error instanceof ErrorWithUserMessage) {
                this.errorMessage = error.message;
            } else if (error instanceof NetworkError) {
                this.pollServerUntilUserIsOnline();
            } else if (error instanceof BadResponseError) {
                log(`Got BadResponseError (${error.code} ${error.message}) when trying to create estimate. This is a bug!`);
            } else {
                shouldNotHappen(error);
            }

            return;
        }

        window.location.href = estimateUrlOrError.right;
    }

    protected created(): void {
        this.lastKnownEstimateState = this.estimate;
    }

    protected mounted(): void {
        if (! this.offlineManager.isInSyncWith(this.lastKnownEstimateState)) {
            this.syncDialog.show();
        }

        this.updateAccessibleSegments();
        this.initializeRouter();
        this.registerEventsRelatedToNotSubmittedParameters();
    }

    protected isLastSegment(): boolean {
        return this.currentSegment === this.segmentConfigurations.length - 1;
    }

    private initializeRouter(): void {
        const segments = this.segmentConfigurations.map(({path}) => ({path}));
        const lastAvailableSegment = this.accessibleSegments.includes(1) ? 1 : 0;
        const updateCurrentSegment = () => {
            this.updateAccessibleSegments();
            this.currentSegment = segments.findIndex(s => s.path === this.$route.path);
            this.$nextTick(() => {
                this.segments[this.currentSegment].beforeEnter();
            });
            this.errorMessage = null;
        };

        this.$router.addRoutes([
            ...segments,
            {path: '*', redirect: () => segments[lastAvailableSegment].path},
        ]);

        this.$router.onReady(() => {
            updateCurrentSegment();

            if (! this.accessibleSegments.includes(this.currentSegment)) {
                this.$router.replace(segments[lastAvailableSegment].path);
                updateCurrentSegment();
            }
        });

        this.$router.beforeEach((to, from, next) => {
            const currentSegment = this.segments[this.currentSegment] || null;

            if (currentSegment === null || !currentSegment.hasNotSubmittedChanges()) {
                next();
                return;
            }

            this.notSubmittedDataDialog.show().then(userWantsToSaveSegment => {
                if (userWantsToSaveSegment) {
                    this.save().then(success => {
                        if (success) {
                            next();
                        }
                    });
                    return;
                }

                currentSegment.clearNotSubmittedChanges();
                next();
            });
        });

        this.$router.afterEach(updateCurrentSegment);
    }

    private save(): Promise<boolean> {
        return this.withSpinner(this.submitCurrentSegment());
    }

    /**
     * Submits current segment and returns TRUE on success
     *
     * This method may throw exception for un-expected errors (expected errors result in FALSE)
     */
    private async submitCurrentSegment(): Promise<boolean> {
        const currentSegment = this.segments[this.currentSegment];

        const result = await currentSegment.submit();

        if (isLeft(result)) {
            const error = result.left;

            if (error instanceof NetworkError) {
                this.pollServerUntilUserIsOnline();

                return false;
            }

            if (error === null) {
                const firstError = currentSegment.$el.querySelector('.invalid-feedback, .is-invalid');

                if (firstError !== null) {
                    VueScrollTo.scrollTo(firstError, 500);
                }

                log('Segment submit has failed');

                return false;
            }

            return shouldNotHappen(error);
        }

        const estimate = result.right;

        if (estimate !== null) {
            this.$emit('save', this.currentSegment, estimate, this.lastKnownEstimateState);
            this.lastKnownEstimateState = estimate;
        }

        return true;
    }

    private pollServerUntilUserIsOnline(): void {
        this.online = false;
        this.offlineManager.waitForConnection()
            .then(estimate => {
                if (this.offlineManager.isInSyncWith(estimate)) {
                    return;
                }

                this.lastKnownEstimateState = estimate;
                this.syncDialog.show();
                this.online = true;
            });
    }

    private registerEventsRelatedToNotSubmittedParameters(): void {
        window.addEventListener('beforeunload', (event: BeforeUnloadEvent) => {
            // Alert user about not submitted parameters before leaving page
            if (Object.keys(this.notSubmittedParameters.get()).length !== 0) {
                event.preventDefault();
                event.returnValue = '';
                return;
            }
        });

        // Clear not submitted parameters if user dismissed the alert
        window.addEventListener('unload', () => this.notSubmittedParameters.clear());
    }

    private withSpinner<TResult>(promise: Promise<TResult>): Promise<TResult> {
        this.loading = true;

        return promise.finally(() => this.loading = false);
    }

    private updateAccessibleSegments(): void {
        const segments = this.segments;

        this.accessibleSegments = [];

        if (segments === undefined) {
            return;
        }

        [...segments.keys()].forEach(key => {
            if (key === 0 || (this.accessibleSegments.includes(key - 1) && segments[key - 1].allowsMovingOn())) {
                this.accessibleSegments.push(key);
            }
        });
    }
}
